import Toastify from 'toastify-js';
import Mediator from '@scripts/core/Mediator.js';
import {t} from './translations.js';

/**
 * ProductManager - Manages product display, pricing, and quantities
 *
 * Responsibilities:
 * - Render product items
 * - Setup product event listeners
 * - Update total price
 * - Handle checkbox interactions
 * - Manage quantity controls (lock/unlock)
 * - Add to cart functionality
 */
export default class ProductManager {
    constructor(rootElement, templateManager, calculatorProducts) {
        this.root = rootElement;
        this.templateManager = templateManager;
        this.calculatorProducts = calculatorProducts;

        // Store promotion settings
        this.promotion = calculatorProducts?.promotion || {
            enabled: false,
            percentage: 0
        };

        // Product quantity lock state
        this.quantityLockState = {
            substrate: {locked: true, defaults: {}},
            drainage: {locked: true, defaults: {}}
        };

        this.selectors = {
            substrateProducts: '[data-substrate-calculator="substrate-products"]',
            drainageProducts: '[data-substrate-calculator="drainage-products"]',
            substrateCheckbox: '[data-substrate-calculator="substrate-checkbox"]',
            drainageCheckbox: '[data-substrate-calculator="drainage-checkbox"]',
            editQuantityBtn: '[data-substrate-calculator="edit-quantity-btn"]',
            addToCartBtn: '[data-substrate-calculator="add-to-cart"]',
            totalPrice: '[data-substrate-calculator="total-price"]',
            originalTotalPrice: '[data-substrate-calculator="original-total-price"]',
            savingsMessage: '[data-substrate-calculator="savings-message"]',
            savingsText: '[data-substrate-calculator="savings-text"]'
        };
    }

    /**
     * Setup product event listeners
     *
     * @param {DocumentFragment} fragment - Results fragment
     */
    setupProductListeners(fragment) {
        const substrateCheckbox = fragment.querySelector(this.selectors.substrateCheckbox);
        const drainageCheckbox = fragment.querySelector(this.selectors.drainageCheckbox);
        const substrateProducts = fragment.querySelector(this.selectors.substrateProducts);
        const drainageProducts = fragment.querySelector(this.selectors.drainageProducts);

        // Substrate checkbox
        if (substrateCheckbox && substrateProducts) {
            substrateCheckbox.addEventListener('change', (e) => {
                const resultsContainer = this.root.querySelector('[data-substrate-calculator="results-container"]');
                if (!resultsContainer) return;

                const warningLow = resultsContainer.querySelector('[data-substrate-calculator="substrate-warning-low"]');
                const warningHigh = resultsContainer.querySelector('[data-substrate-calculator="substrate-warning-high"]');
                const productCards = resultsContainer.querySelectorAll('[data-product-type="substrate"]');

                if (e.target.checked) {
                    if (warningLow) warningLow.classList.add('hidden');
                    if (warningHigh) warningHigh.classList.add('hidden');
                    productCards.forEach(card => {
                        card.classList.remove('opacity-30');
                        const qtyInput = card.querySelector('[data-substrate-calculator="product-qty"]');
                        if (qtyInput) {
                            qtyInput.classList.remove('text-red-600');
                        }
                    });
                    this.validateQuantities(null, 'substrate');
                } else {
                    if (warningLow) warningLow.classList.remove('hidden');
                    if (warningHigh) warningHigh.classList.add('hidden');
                    productCards.forEach(card => {
                        card.classList.add('opacity-30');
                        const qtyInput = card.querySelector('[data-substrate-calculator="product-qty"]');
                        if (qtyInput) {
                            qtyInput.classList.add('text-red-600');
                        }
                    });
                    this.resetQuantitiesToDefault(null, 'substrate');
                }
                this.updateTotalPrice(null);
            });
        }

        // Drainage checkbox
        if (drainageCheckbox && drainageProducts) {
            drainageCheckbox.addEventListener('change', (e) => {
                const resultsContainer = this.root.querySelector('[data-substrate-calculator="results-container"]');
                if (!resultsContainer) return;

                const warningLow = resultsContainer.querySelector('[data-substrate-calculator="drainage-warning-low"]');
                const warningHigh = resultsContainer.querySelector('[data-substrate-calculator="drainage-warning-high"]');
                const productCards = resultsContainer.querySelectorAll('[data-product-type="drainage"]');

                if (e.target.checked) {
                    if (warningLow) warningLow.classList.add('hidden');
                    if (warningHigh) warningHigh.classList.add('hidden');
                    productCards.forEach(card => {
                        card.classList.remove('opacity-30');
                        const qtyInput = card.querySelector('[data-substrate-calculator="product-qty"]');
                        if (qtyInput) {
                            qtyInput.classList.remove('text-red-600');
                        }
                    });
                    this.validateQuantities(null, 'drainage');
                } else {
                    if (warningLow) warningLow.classList.remove('hidden');
                    if (warningHigh) warningHigh.classList.add('hidden');
                    productCards.forEach(card => {
                        card.classList.add('opacity-30');
                        const qtyInput = card.querySelector('[data-substrate-calculator="product-qty"]');
                        if (qtyInput) {
                            qtyInput.classList.add('text-red-600');
                        }
                    });
                    this.resetQuantitiesToDefault(null, 'drainage');
                }
                this.updateTotalPrice(null);
            });
        }

        // "Zmień ilość" buttons
        const editQtyButtons = fragment.querySelectorAll(this.selectors.editQuantityBtn);
        editQtyButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const productType = btn.dataset.productType;
                this.toggleQuantityLock(null, productType);
            });
        });

        // Quantity minus buttons
        const minusButtons = fragment.querySelectorAll('[data-qty-minus]');
        minusButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                if (btn.dataset.locked === 'true') return;

                const input = btn.closest('.inline-flex').querySelector('input[type="number"]');
                if (input) {
                    const currentValue = parseInt(input.value) || 0;
                    if (currentValue > 0) {
                        input.value = currentValue - 1;
                        this.updateTotalPrice(null);
                        this.updateCheckboxLabelFromDOM(null);
                        const productType = input.dataset.productType;
                        this.validateQuantities(null, productType);
                        this.updateMinusButtonsState(null, productType);
                    }
                }
            });
        });

        // Quantity plus buttons
        const plusButtons = fragment.querySelectorAll('[data-qty-plus]');
        plusButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                if (btn.dataset.locked === 'true') return;

                const input = btn.closest('.inline-flex').querySelector('input[type="number"]');
                if (input) {
                    const currentValue = parseInt(input.value) || 0;
                    input.value = currentValue + 1;
                    this.updateTotalPrice(null);
                    this.updateCheckboxLabelFromDOM(null);
                    const productType = input.dataset.productType;
                    this.validateQuantities(null, productType);
                    this.updateMinusButtonsState(null, productType);
                }
            });
        });

        // Quantity input change listeners
        const qtyInputs = fragment.querySelectorAll('[data-substrate-calculator="product-qty"]');
        qtyInputs.forEach(input => {
            input.addEventListener('change', () => {
                const productType = input.dataset.productType;
                this.updateTotalPrice(null);
                this.updateCheckboxLabelFromDOM(null);
                this.validateQuantities(null, productType);
                this.updateMinusButtonsState(null, productType);
            });
        });

        // Add to cart button
        const addToCartBtn = fragment.querySelector(this.selectors.addToCartBtn);
        if (addToCartBtn) {
            addToCartBtn.addEventListener('click', () => this.addToCart(null));
        }

        // Toggle areas button
        const toggleAreasBtn = fragment.querySelector('[data-substrate-calculator="toggle-areas-btn"]');
        if (toggleAreasBtn) {
            toggleAreasBtn.addEventListener('click', (e) => {
                const container = this.root.querySelector('[data-substrate-calculator="results-container"]');
                if (!container) return;

                const areaCards = container.querySelector('[data-substrate-calculator="area-cards"]');
                const toggleText = container.querySelector('[data-substrate-calculator="toggle-areas-text"]');
                const toggleIcon = container.querySelector('[data-substrate-calculator="toggle-areas-icon"]');

                if (areaCards) {
                    const isHidden = areaCards.classList.contains('hidden');

                    if (isHidden) {
                        // Show areas
                        areaCards.classList.remove('hidden');
                        if (toggleText) toggleText.textContent = t('collapse_details');
                        if (toggleIcon) {
                            toggleIcon.setAttribute('width', '14');
                            toggleIcon.setAttribute('height', '8');
                            toggleIcon.setAttribute('viewBox', '0 0 14 8');
                            toggleIcon.innerHTML = '<path d="M13 7L7 1L1 7" stroke="#E81A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
                        }
                    } else {
                        // Hide areas
                        areaCards.classList.add('hidden');
                        if (toggleText) toggleText.textContent = t('expand_details');
                        if (toggleIcon) {
                            toggleIcon.setAttribute('width', '14');
                            toggleIcon.setAttribute('height', '8');
                            toggleIcon.setAttribute('viewBox', '0 0 14 8');
                            toggleIcon.innerHTML = '<path d="M1 1L7 7L13 1" stroke="#E81A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
                        }
                    }
                }
            });
        }
    }

    /**
     * Render product items using TemplateManager
     *
     * @param {HTMLElement} container - Container element
     * @param {Object} packaging - Packaging data
     * @param {string} type - Product type
     */
    renderProductItems(container, packaging, type) {
        console.log(`[ProductManager] renderProductItems START: type=${type}, totalVolume=${packaging.totalVolume}`);
        if (!container || packaging.totalVolume <= 0) {
            console.log(`[ProductManager] Skipping rendering: container=${!!container}, totalVolume=${packaging.totalVolume}`);
            if (container) {
                container.innerHTML = '<p class="text-sm text-gray-500 italic">Brak produktów</p>';
            }
            return;
        }

        const isLocked = this.quantityLockState[type].locked;

        // Store default values for reset
        this.quantityLockState[type].defaults = {
            pallets: packaging.pallets,
            bags: packaging.bags,
            mediumBags: packaging.mediumBags || 0,
            smallBags: packaging.smallBags || 0
        };

        container.innerHTML = '';

        // Track product IDs to prevent duplicates
        const renderedProductIds = new Set();

        // Get the actual bag sizes used (for finding correct products)
        const usedBagSizes = packaging.usedBagSizes || {};
        console.log(`[ProductManager] renderProductItems: type=${type}, usedBagSizes:`, usedBagSizes);
        console.log(`[ProductManager] Packaging breakdown: pallets=${packaging.pallets}, bags=${packaging.bags}, mediumBags=${packaging.mediumBags}, smallBags=${packaging.smallBags}`);
        console.log(`[ProductManager] Available calculator products:`, this.calculatorProducts ? {
            hasDynamicSizes: !!(this.calculatorProducts.dynamic_substrate_sizes?.length),
            dynamicSizesCount: this.calculatorProducts.dynamic_substrate_sizes?.length || 0,
            hasSubstrateBag: !!(this.calculatorProducts.substrate_bag?.length),
            substrateBagCount: this.calculatorProducts.substrate_bag?.length || 0
        } : 'NO PRODUCTS');

        // Helper function to add product item if not duplicate
        const addProductIfNotDuplicate = (product, productUnit, quantity) => {
            if (!product || !product.id) return;
            if (renderedProductIds.has(product.id)) {
                console.warn(`[ProductManager] Skipping duplicate product ID: ${product.id} (${product.name})`);
                return;
            }
            renderedProductIds.add(product.id);

            const productItem = this.templateManager.createProductItem(
                product,
                type,
                productUnit,
                quantity,
                isLocked
            );
            if (productItem) {
                container.appendChild(productItem);
            }
        };

        // Use bagsBySize if available (detailed breakdown), otherwise fall back to category-based rendering
        if (packaging.bagsBySize && Object.keys(packaging.bagsBySize).length > 0) {
            console.log(`[ProductManager] Using bagsBySize for rendering:`, packaging.bagsBySize);
            // Render each bag size separately
            for (const [sizeStr, count] of Object.entries(packaging.bagsBySize)) {
                const bagSize = parseInt(sizeStr);
                const product = this.findProductBySize(type, bagSize);
                if (product && product.id) {
                    // Determine product unit based on size
                    let productUnit;
                    if (bagSize >= 1000) {
                        productUnit = 'pallet';
                    } else if (bagSize >= 50) {
                        productUnit = 'bag';
                    } else if (bagSize >= 15) {
                        productUnit = 'medium_bag';
                    } else {
                        productUnit = 'small_bag';
                    }
                    addProductIfNotDuplicate(product, productUnit, count);
                }
            }
        } else {
            // Fall back to category-based rendering (for drainage or when bagsBySize not available)
            console.log(`[ProductManager] Using category-based rendering`);
            // Pallets/Big bags
            if (packaging.pallets > 0) {
                const palletSize = usedBagSizes.pallet || 1200;
                const product = this.findProductBySize(type, palletSize) || {};
                addProductIfNotDuplicate(product, 'pallet', packaging.pallets);
            }

            // Bags (50L category)
            if (packaging.bags > 0) {
                const bagSize = usedBagSizes.bag || 50;
                const product = this.findProductBySize(type, bagSize) || {};
                addProductIfNotDuplicate(product, 'bag', packaging.bags);
            }

            // Medium bags (20L category) - includes 10L, 15L if they fall here
            if (packaging.mediumBags > 0) {
                const mediumBagSize = usedBagSizes.mediumBag || 20;
                const product = this.findProductBySize(type, mediumBagSize) || {};
                addProductIfNotDuplicate(product, 'medium_bag', packaging.mediumBags);
            }

            // Small bags (5L category)
            if (packaging.smallBags > 0) {
                const smallBagSize = usedBagSizes.smallBag || 5;
                const product = this.findProductBySize(type, smallBagSize) || {};
                addProductIfNotDuplicate(product, 'small_bag', packaging.smallBags);
            }
        }

        console.log(`[ProductManager] renderProductItems END: type=${type}, renderedProductIds=${Array.from(renderedProductIds)}, container has ${container.children.length} children`);

        // Update minus button states after rendering
        this.updateMinusButtonsState(null, type);
    }

    /**
     * Update minus button states based on input values
     *
     * @param {DocumentFragment} fragment - Results fragment (optional, will query root if null)
     * @param {string} productType - Product type (optional, if null updates all)
     */
    updateMinusButtonsState(fragment = null, productType = null) {
        let container;
        if (fragment && fragment.hasChildNodes()) {
            container = fragment;
        } else {
            container = this.root.querySelector('[data-substrate-calculator="results-container"]');
            if (!container) return;
        }

        const types = productType ? [productType] : ['substrate', 'drainage'];

        types.forEach(type => {
            const productContainers = container.querySelectorAll(`[data-product-type="${type}"]`);
            productContainers.forEach(prodContainer => {
                const minusBtn = prodContainer.querySelector('[data-qty-minus]');
                const input = prodContainer.querySelector('input[type="number"]');

                if (minusBtn && input) {
                    const currentValue = parseInt(input.value) || 0;
                    if (currentValue <= 0) {
                        minusBtn.disabled = true;
                        minusBtn.classList.add('opacity-50', 'cursor-not-allowed');
                    } else {
                        minusBtn.disabled = false;
                        minusBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                    }
                }
            });
        });
    }

    /**
     * Update total price display
     *
     * @param {DocumentFragment} fragment - Results fragment (optional, will query root if not provided or empty)
     */
    updateTotalPrice(fragment = null) {
        let originalTotalPrice = 0;
        let promotionalTotalPrice = 0;

        // Determine whether to use fragment or root
        // After appendChild, fragment is empty, so use root
        let container;
        if (fragment && fragment.hasChildNodes()) {
            // Fragment has content, use it
            container = fragment;
        } else {
            // Fragment is empty or not provided, query root
            container = this.root.querySelector('[data-substrate-calculator="results-container"]');
            if (!container) {
                console.error('Results container not found in DOM for price update');
                return;
            }
        }

        const substrateCheckbox = container.querySelector(this.selectors.substrateCheckbox);
        const drainageCheckbox = container.querySelector(this.selectors.drainageCheckbox);
        const substrateChecked = substrateCheckbox?.checked ?? false;
        const drainageChecked = drainageCheckbox?.checked ?? false;

        // Calculate substrate products
        if (substrateChecked) {
            const substrateContainers = container.querySelectorAll('[data-product-type="substrate"]');
            substrateContainers.forEach(prodContainer => {
                const priceEl = prodContainer.querySelector('[data-product-price]');
                const originalPriceEl = prodContainer.querySelector('[data-product-original-price]');
                const input = prodContainer.querySelector('input[type="number"]');
                const quantity = parseInt(input?.value) || 0;

                const promotionalPrice = parseFloat(priceEl?.dataset.productPrice || 0);
                const originalPrice = parseFloat(originalPriceEl?.dataset.productOriginalPrice || promotionalPrice);

                originalTotalPrice += originalPrice * quantity;
                promotionalTotalPrice += promotionalPrice * quantity;
            });
        }

        // Calculate drainage products
        if (drainageChecked) {
            const drainageContainers = container.querySelectorAll('[data-product-type="drainage"]');
            drainageContainers.forEach(prodContainer => {
                const priceEl = prodContainer.querySelector('[data-product-price]');
                const originalPriceEl = prodContainer.querySelector('[data-product-original-price]');
                const input = prodContainer.querySelector('input[type="number"]');
                const quantity = parseInt(input?.value) || 0;

                const promotionalPrice = parseFloat(priceEl?.dataset.productPrice || 0);
                const originalPrice = parseFloat(originalPriceEl?.dataset.productOriginalPrice || promotionalPrice);

                originalTotalPrice += originalPrice * quantity;
                promotionalTotalPrice += promotionalPrice * quantity;
            });
        }

        // Get price elements
        const totalPriceEl = container.querySelector(this.selectors.totalPrice);
        const originalTotalPriceEl = container.querySelector(this.selectors.originalTotalPrice);
        const savingsMessageEl = container.querySelector(this.selectors.savingsMessage);
        const savingsTextEl = container.querySelector(this.selectors.savingsText);

        // Check if promotion is active
        const hasPromotion = this.promotion.enabled && this.promotion.percentage > 0 && originalTotalPrice > promotionalTotalPrice;

        // Update total price (always show current/promotional price)
        if (totalPriceEl) {
            totalPriceEl.textContent = `${promotionalTotalPrice.toFixed(2).replace('.', ',')} ${t('currency')}`;
        }

        // Show/hide original price based on promotion
        if (originalTotalPriceEl) {
            if (hasPromotion) {
                originalTotalPriceEl.textContent = `${originalTotalPrice.toFixed(2).replace('.', ',')} ${t('currency')}`;
                originalTotalPriceEl.classList.remove('hidden');
            } else {
                originalTotalPriceEl.classList.add('hidden');
            }
        }

        // Show/hide savings message based on promotion
        if (savingsMessageEl) {
            if (hasPromotion) {
                savingsMessageEl.classList.remove('hidden');
                if (savingsTextEl) {
                    savingsTextEl.textContent = `-${this.promotion.percentage}%`;
                }
            } else {
                savingsMessageEl.classList.add('hidden');
            }
        }
    }

    /**
     * Update checkbox label from DOM
     *
     * @param {DocumentFragment} fragment - Results fragment (optional, will query root if null or empty)
     */
    updateCheckboxLabelFromDOM(fragment = null) {
        // Determine whether to use fragment or root
        let container;
        if (fragment && fragment.hasChildNodes()) {
            container = fragment;
        } else {
            container = this.root.querySelector('[data-substrate-calculator="results-container"]');
            if (!container) return;
        }

        // Update substrate label
        const substrateLabel = container.querySelector('[data-substrate-calculator="substrate-label"]');
        if (substrateLabel) {
            const packaging = this.getPackagingFromDOM(container, 'substrate');
            if (packaging) {
                const breakdownText = this.formatPackagingBreakdown(packaging, 'ziemi', 'substrate');
                substrateLabel.innerHTML = breakdownText;
            }
        }

        // Update drainage label
        const drainageLabel = container.querySelector('[data-substrate-calculator="drainage-label"]');
        if (drainageLabel) {
            const packaging = this.getPackagingFromDOM(container, 'drainage');
            if (packaging) {
                const breakdownText = this.formatPackagingBreakdown(packaging, 'keramzytu', 'drainage');
                drainageLabel.innerHTML = breakdownText;
            }
        }
    }

    /**
     * Get packaging from DOM
     *
     * @param {DocumentFragment|HTMLElement} container - Container element
     * @param {string} type - Product type
     * @returns {Object} Packaging data
     */
    getPackagingFromDOM(container, type) {
        const palletContainer = container.querySelector(`[data-product-type="${type}"][data-product-unit="pallet"]`);
        const bagContainer = container.querySelector(`[data-product-type="${type}"][data-product-unit="bag"]`);
        const mediumBagContainer = container.querySelector(`[data-product-type="${type}"][data-product-unit="medium_bag"]`);
        const smallBagContainer = container.querySelector(`[data-product-type="${type}"][data-product-unit="small_bag"]`);

        let pallets = 0;
        let bags = 0;
        let mediumBags = 0;
        let smallBags = 0;

        // Get actual bag sizes from data attributes (set by TemplateManager)
        // Fallback to hardcoded values only if data attribute is missing
        const palletSizeAttr = palletContainer?.getAttribute('data-bag-size');
        const bagSizeAttr = bagContainer?.getAttribute('data-bag-size');
        const mediumBagSizeAttr = mediumBagContainer?.getAttribute('data-bag-size');
        const smallBagSizeAttr = smallBagContainer?.getAttribute('data-bag-size');

        const PALLET_SIZE = palletSizeAttr ? parseInt(palletSizeAttr) : (type === 'drainage' ? 2000 : 1200);
        const BAG_SIZE = bagSizeAttr ? parseInt(bagSizeAttr) : 50;
        const MEDIUM_BAG_SIZE = mediumBagSizeAttr ? parseInt(mediumBagSizeAttr) : 20;
        const SMALL_BAG_SIZE = smallBagSizeAttr ? parseInt(smallBagSizeAttr) : (type === 'substrate' ? 5 : 4);

        if (palletContainer) {
            const input = palletContainer.querySelector('input[type="number"]');
            if (input) {
                pallets = parseInt(input.value) || 0;
            }
        }

        if (bagContainer) {
            const input = bagContainer.querySelector('input[type="number"]');
            if (input) {
                bags = parseInt(input.value) || 0;
            }
        }

        if (mediumBagContainer) {
            const input = mediumBagContainer.querySelector('input[type="number"]');
            if (input) {
                mediumBags = parseInt(input.value) || 0;
            }
        }

        if (smallBagContainer) {
            const input = smallBagContainer.querySelector('input[type="number"]');
            if (input) {
                smallBags = parseInt(input.value) || 0;
            }
        }

        const totalVolume = (pallets * PALLET_SIZE) + (bags * BAG_SIZE) + (mediumBags * MEDIUM_BAG_SIZE) + (smallBags * SMALL_BAG_SIZE);

        return {
            pallets,
            bags,
            mediumBags,
            smallBags,
            totalVolume,
            // Include actual bag sizes used for display in formatPackagingBreakdown
            usedBagSizes: {
                pallet: PALLET_SIZE,
                bag: bagContainer ? BAG_SIZE : 0,
                mediumBag: mediumBagContainer ? MEDIUM_BAG_SIZE : 0,
                smallBag: smallBagContainer ? SMALL_BAG_SIZE : 0
            }
        };
    }

    /**
     * Format packaging breakdown text
     *
     * @param {Object} packaging - Packaging object
     * @param {string} materialName - Material name (unused, kept for compatibility)
     * @param {string} type - Product type
     * @returns {string} Formatted HTML
     */
    formatPackagingBreakdown(packaging, materialName, type) {
        const parts = [];

        // Get actual bag sizes used (from smart packaging) if available
        const usedBagSizes = packaging.usedBagSizes || {};

        if (type === 'drainage') {
            if (packaging.pallets > 0) {
                parts.push(`${packaging.pallets} x ${t('big_bag_2000_l')}`);
            }
        } else {
            if (packaging.pallets > 0) {
                parts.push(`${packaging.pallets} x ${t('pallet_1200_l')}`);
            }
        }

        if (packaging.bags > 0) {
            const bagSize = usedBagSizes.bag || 50;
            const translationKey = bagSize === 1500 ? 'big_bag_1500_l' : 'bag_50_l';
            parts.push(`${packaging.bags}x ${t(translationKey)}`);
        }

        if (packaging.mediumBags > 0) {
            const mediumBagSize = usedBagSizes.mediumBag || 20;
            const translationKey = mediumBagSize === 20 ? 'bag_20_l' : `bag_${mediumBagSize}_l`;
            parts.push(`${packaging.mediumBags}x ${t(translationKey)}`);
        }

        if (packaging.smallBags > 0) {
            const smallBagSize = usedBagSizes.smallBag || (type === 'drainage' ? 4 : 5);
            let translationKey;
            if (smallBagSize === 5) {
                translationKey = 'bag_5_l';
            } else if (smallBagSize === 4) {
                translationKey = 'bag_4_l';
            } else if (smallBagSize === 10) {
                translationKey = 'bag_10_l';
            } else if (smallBagSize === 15) {
                translationKey = 'bag_15_l';
            } else {
                translationKey = `bag_${smallBagSize}_l`;
            }
            parts.push(`${packaging.smallBags}x ${t(translationKey)}`);
        }

        const breakdown = parts.length > 0 ? ` (${parts.join(' + ')})` : '';
        // Show totalVolume (actual packaging volume with bags), not displayVolume (original user need)
        const displayVol = packaging.totalVolume;
        const volumeText = Math.ceil(displayVol).toLocaleString('pl-PL');
        const materialLabel = type === 'drainage' ? t('drainage_expanded_clay') : t('substrate_base');

        return `${materialLabel}: <span class="font-bold">${volumeText} L</span><span class="text-gray-700">${breakdown}</span>`;
    }

    /**
     * Reset quantities to default values
     *
     * @param {DocumentFragment} fragment - Results fragment (optional, will query root if null or empty)
     * @param {string} productType - Product type
     */
    resetQuantitiesToDefault(fragment, productType) {
        const defaults = this.quantityLockState[productType].defaults;
        if (!defaults) return;

        // Determine whether to use fragment or root
        let container;
        if (fragment && fragment.hasChildNodes()) {
            container = fragment;
        } else {
            container = this.root.querySelector('[data-substrate-calculator="results-container"]');
            if (!container) {
                console.error('Results container not found in DOM for reset');
                return;
            }
        }

        const productContainers = container.querySelectorAll(`[data-product-type="${productType}"]`);

        productContainers.forEach(prodContainer => {
            const unit = prodContainer.dataset.productUnit;
            const input = prodContainer.querySelector('input[type="number"]');

            if (input && unit) {
                let defaultValue = 0;
                if (unit === 'pallet') {
                    defaultValue = defaults.pallets || 0;
                } else if (unit === 'bag') {
                    defaultValue = defaults.bags || 0;
                } else if (unit === 'medium_bag') {
                    defaultValue = defaults.mediumBags || 0;
                } else if (unit === 'small_bag') {
                    defaultValue = defaults.smallBags || 0;
                }
                input.value = defaultValue;
            }
        });

        this.updateTotalPrice(null);
        this.updateCheckboxLabelFromDOM(null);
    }

    /**
     * Toggle quantity lock state
     *
     * @param {DocumentFragment} fragment - Results fragment (will query root if empty)
     * @param {string} productType - Product type
     */
    toggleQuantityLock(fragment, productType) {
        const isLocked = this.quantityLockState[productType].locked ?? true;
        this.quantityLockState[productType].locked = !isLocked;

        // Use root to find elements since fragment may be empty after appendChild
        const resultsContainer = this.root.querySelector('[data-substrate-calculator="results-container"]');
        if (!resultsContainer) {
            console.error('Results container not found in DOM');
            return;
        }

        const productContainers = resultsContainer.querySelectorAll(`[data-product-type="${productType}"]`);
        const editBtns = resultsContainer.querySelectorAll(`${this.selectors.editQuantityBtn}[data-product-type="${productType}"]`);

        productContainers.forEach(container => {
            const minusBtn = container.querySelector('[data-qty-minus]');
            const plusBtn = container.querySelector('[data-qty-plus]');
            const input = container.querySelector('input[type="number"]');
            const qtyControls = container.querySelector('.qty-controls');

            if (!isLocked) {
                // Lock again
                if (minusBtn) {
                    minusBtn.disabled = true;
                    minusBtn.dataset.locked = 'true';
                }
                if (plusBtn) {
                    plusBtn.disabled = true;
                    plusBtn.dataset.locked = 'true';
                }
                if (input) {
                    input.readOnly = true;
                    input.classList.remove('text-success-500');
                    input.classList.add('bg-gray-50', 'cursor-not-allowed');
                }
                if (qtyControls) {
                    qtyControls.classList.add('hidden');
                }
                editBtns.forEach(btn => btn.classList.remove('opacity-40'));
            } else {
                // Unlock
                // Check if warning is visible before changing colors
                const warningLow = resultsContainer.querySelector(`[data-substrate-calculator="${productType}-warning-low"]`);
                const isWarningVisible = warningLow && !warningLow.classList.contains('hidden');

                if (minusBtn) {
                    minusBtn.disabled = false;
                    minusBtn.dataset.locked = 'false';
                }
                if (plusBtn) {
                    plusBtn.disabled = false;
                    plusBtn.dataset.locked = 'false';
                }
                if (input) {
                    input.readOnly = false;
                    input.classList.remove('bg-gray-50', 'cursor-not-allowed');
                    if (isWarningVisible) {
                        // Keep red color if warning is visible
                        input.classList.add('text-red-600');
                    } else {
                        // Use green color if no warning
                        input.classList.add('text-success-500');
                    }
                }
                if (qtyControls) {
                    qtyControls.classList.remove('hidden');
                }
                editBtns.forEach(btn => btn.classList.add('opacity-40'));
            }
        });

        this.updateCheckboxLabelFromDOM(null);
    }

    /**
     * Validate quantities and show appropriate warnings
     *
     * @param {DocumentFragment} fragment - Results fragment (optional, will query root if null)
     * @param {string} productType - Product type (substrate/drainage)
     */
    validateQuantities(fragment, productType) {
        const resultsContainer = this.root.querySelector('[data-substrate-calculator="results-container"]');
        if (!resultsContainer) return;

        const checkbox = resultsContainer.querySelector(
            productType === 'substrate' ? this.selectors.substrateCheckbox : this.selectors.drainageCheckbox
        );

        if (!checkbox || !checkbox.checked) return;

        const warningLow = resultsContainer.querySelector(`[data-substrate-calculator="${productType}-warning-low"]`);
        const warningHigh = resultsContainer.querySelector(`[data-substrate-calculator="${productType}-warning-high"]`);
        const recommendedVolumeSpan = warningHigh?.querySelector('[data-recommended-volume]');

        const defaults = this.quantityLockState[productType].defaults;
        if (!defaults) return;

        const recommendedPallets = defaults.pallets || 0;
        const recommendedBags = defaults.bags || 0;
        const recommendedMediumBags = defaults.mediumBags || 0;
        const recommendedSmallBags = defaults.smallBags || 0;

        // Get actual bag sizes from data attributes
        const palletContainer = resultsContainer.querySelector(`[data-product-type="${productType}"][data-product-unit="pallet"]`);
        const bagContainer = resultsContainer.querySelector(`[data-product-type="${productType}"][data-product-unit="bag"]`);
        const mediumBagContainer = resultsContainer.querySelector(`[data-product-type="${productType}"][data-product-unit="medium_bag"]`);
        const smallBagContainer = resultsContainer.querySelector(`[data-product-type="${productType}"][data-product-unit="small_bag"]`);

        const palletSizeAttr = palletContainer?.getAttribute('data-bag-size');
        const bagSizeAttr = bagContainer?.getAttribute('data-bag-size');
        const mediumBagSizeAttr = mediumBagContainer?.getAttribute('data-bag-size');
        const smallBagSizeAttr = smallBagContainer?.getAttribute('data-bag-size');

        const PALLET_SIZE = palletSizeAttr ? parseInt(palletSizeAttr) : (productType === 'drainage' ? 2000 : 1200);
        const BAG_SIZE = bagSizeAttr ? parseInt(bagSizeAttr) : 50;
        const MEDIUM_BAG_SIZE = mediumBagSizeAttr ? parseInt(mediumBagSizeAttr) : 20;
        const SMALL_BAG_SIZE = smallBagSizeAttr ? parseInt(smallBagSizeAttr) : (productType === 'substrate' ? 5 : 4);

        const recommendedVolume = (recommendedPallets * PALLET_SIZE) + (recommendedBags * BAG_SIZE) + (recommendedMediumBags * MEDIUM_BAG_SIZE) + (recommendedSmallBags * SMALL_BAG_SIZE);

        // Get current quantities
        let currentPallets = 0;
        let currentBags = 0;
        let currentMediumBags = 0;
        let currentSmallBags = 0;

        if (palletContainer) {
            const input = palletContainer.querySelector('input[type="number"]');
            if (input) currentPallets = parseInt(input.value) || 0;
        }

        if (bagContainer) {
            const input = bagContainer.querySelector('input[type="number"]');
            if (input) currentBags = parseInt(input.value) || 0;
        }

        if (mediumBagContainer) {
            const input = mediumBagContainer.querySelector('input[type="number"]');
            if (input) currentMediumBags = parseInt(input.value) || 0;
        }

        if (smallBagContainer) {
            const input = smallBagContainer.querySelector('input[type="number"]');
            if (input) currentSmallBags = parseInt(input.value) || 0;
        }

        const currentVolume = (currentPallets * PALLET_SIZE) + (currentBags * BAG_SIZE) + (currentMediumBags * MEDIUM_BAG_SIZE) + (currentSmallBags * SMALL_BAG_SIZE);

        // Hide all warnings first
        if (warningLow) warningLow.classList.add('hidden');
        if (warningHigh) warningHigh.classList.add('hidden');

        // Show appropriate warning
        if (currentVolume < recommendedVolume) {
            if (warningLow) warningLow.classList.remove('hidden');
            // Add red color to quantity inputs when volume is too low
            // Warning is now visible, so always use red color (even in edit mode)
            const productCards = resultsContainer.querySelectorAll(`[data-product-type="${productType}"]`);
            productCards.forEach(card => {
                const qtyInput = card.querySelector('[data-substrate-calculator="product-qty"]');
                if (qtyInput) {
                    qtyInput.classList.add('text-red-600');
                    qtyInput.classList.remove('text-success-500');
                }
            });
        } else if (currentVolume > recommendedVolume) {
            if (warningHigh && recommendedVolumeSpan) {
                // Format recommended volume text (e.g., "200 L (4x worek 50L)")
                const parts = [];
                if (recommendedPallets > 0) {
                    parts.push(`${recommendedPallets}x ${productType === 'drainage' ? t('big_bag_2000_l') : t('pallet_1200_l')}`);
                }
                if (recommendedBags > 0) {
                    parts.push(`${recommendedBags}x ${t('bag_50_l')}`);
                }
                if (recommendedMediumBags > 0) {
                    parts.push(`${recommendedMediumBags}x ${t('bag_20_l')}`);
                }
                if (recommendedSmallBags > 0) {
                    // Use 5L for substrate, 4L for drainage
                    const smallBagKey = productType === 'drainage' ? 'bag_4_l' : 'bag_5_l';
                    parts.push(`${recommendedSmallBags}x ${t(smallBagKey)}`);
                }
                const volumeText = `${Math.ceil(recommendedVolume).toLocaleString('pl-PL')} L`;
                const breakdown = parts.length > 0 ? ` (${parts.join(' + ')})` : '';
                recommendedVolumeSpan.textContent = `${volumeText}${breakdown}`;
            }
            if (warningHigh) warningHigh.classList.remove('hidden');
        } else {
            // Volume is exactly as recommended or higher - remove red color
            const productCards = resultsContainer.querySelectorAll(`[data-product-type="${productType}"]`);
            productCards.forEach(card => {
                const qtyInput = card.querySelector('[data-substrate-calculator="product-qty"]');
                if (qtyInput) {
                    qtyInput.classList.remove('text-red-600');
                    // Add green if unlocked (in edit mode)
                    if (!this.quantityLockState[productType].locked) {
                        qtyInput.classList.add('text-success-500');
                    }
                }
            });
        }
    }

    /**
     * Get products by type and unit
     *
     * @param {string} type - Product type (substrate/drainage)
     * @param {string} unit - Product unit (pallet/bag/small_bag)
     * @returns {Array} Products array
     */
    getProducts(type, unit) {
        const key = `${type}_${unit}`;
        return this.calculatorProducts?.[key] || [];
    }

    /**
     * Find product by specific size
     * Searches in both standard categories and dynamic sizes
     *
     * @param {string} type - Product type (substrate/drainage)
     * @param {number} size - Bag size in liters
     * @returns {Object|null} Product object or null
     */
    findProductBySize(type, size) {
        console.log(`[ProductManager] findProductBySize: type=${type}, size=${size}L`);

        // Only check dynamic substrate sizes for substrate type (not drainage!)
        const dynamicSizes = (type === 'substrate') ? this.calculatorProducts?.dynamic_substrate_sizes : null;
        if (dynamicSizes && Array.isArray(dynamicSizes)) {
            console.log(`[ProductManager] Checking ${dynamicSizes.length} dynamic substrate sizes:`, dynamicSizes.map(s => `${s.size}L (${s.products?.length || 0} products)`));
            for (const sizeInfo of dynamicSizes) {
                if (sizeInfo.size === size && sizeInfo.products && sizeInfo.products.length > 0) {
                    console.log(`[ProductManager] Found exact match in dynamic sizes: ${sizeInfo.size}L ->`, sizeInfo.products[0].name);
                    return sizeInfo.products[0];
                }
            }
            console.log(`[ProductManager] No exact match in dynamic sizes for ${size}L`);
        }

        // Fallback to standard categories
        console.log(`[ProductManager] Fallback to standard categories for ${size}L`);
        if (size >= 50) {
            const products = this.getProducts(type, 'bag');
            console.log(`[ProductManager] 'bag' category has ${products.length} products:`, products.map(p => `${p.name} (${p.unit_price_factor}L)`));
            // Find product matching this size
            const matching = products.find(p => p.unit_price_factor == size);
            if (matching) {
                console.log(`[ProductManager] Found matching product in 'bag' category:`, matching.name);
                return matching;
            }
            if (products.length > 0) {
                console.log(`[ProductManager] No exact match, returning first 'bag' product:`, products[0].name);
                return products[0];
            }
        } else if (size >= 15) {
            const products = this.getProducts(type, 'medium_bag');
            console.log(`[ProductManager] 'medium_bag' category has ${products.length} products:`, products.map(p => `${p.name} (${p.unit_price_factor}L)`));
            const matching = products.find(p => p.unit_price_factor == size);
            if (matching) {
                console.log(`[ProductManager] Found matching product in 'medium_bag' category:`, matching.name);
                return matching;
            }
            if (products.length > 0) {
                console.log(`[ProductManager] No exact match, returning first 'medium_bag' product:`, products[0].name);
                return products[0];
            }
        } else {
            const products = this.getProducts(type, 'small_bag');
            console.log(`[ProductManager] 'small_bag' category has ${products.length} products:`, products.map(p => `${p.name} (${p.unit_price_factor}L)`));
            const matching = products.find(p => p.unit_price_factor == size);
            if (matching) {
                console.log(`[ProductManager] Found matching product in 'small_bag' category:`, matching.name);
                return matching;
            }
            if (products.length > 0) {
                console.log(`[ProductManager] No exact match, returning first 'small_bag' product:`, products[0].name);
                return products[0];
            }
        }

        console.log(`[ProductManager] No product found for ${size}L, returning null`);
        return null;
    }

    /**
     * Get selected products from checked checkboxes
     *
     * @param {DocumentFragment} fragment - Results fragment (optional, will query root if not provided or empty)
     * @returns {Array} Selected products
     */
    getSelectedProducts(fragment = null) {
        const products = [];

        // Determine whether to use fragment or root
        // After appendChild, fragment is empty, so use root
        let container;
        if (fragment && fragment.hasChildNodes()) {
            // Fragment has content, use it
            container = fragment;
        } else {
            // Fragment is empty or not provided, query root
            container = this.root.querySelector('[data-substrate-calculator="results-container"]');
            if (!container) {
                console.error('Results container not found in DOM for getSelectedProducts');
                return products;
            }
        }

        const substrateCheckbox = container.querySelector(this.selectors.substrateCheckbox);
        const drainageCheckbox = container.querySelector(this.selectors.drainageCheckbox);

        const substrateChecked = substrateCheckbox?.checked ?? false;
        const drainageChecked = drainageCheckbox?.checked ?? false;

        // Get substrate products
        if (substrateChecked) {
            const substrateContainers = container.querySelectorAll('[data-product-type="substrate"]');
            substrateContainers.forEach(prodContainer => {
                const qtyInput = prodContainer.querySelector('input[type="number"]');
                const priceEl = prodContainer.querySelector('[data-product-price]');
                const originalPriceEl = prodContainer.querySelector('[data-product-original-price]');
                const productId = qtyInput?.dataset.productId || '';
                const quantity = parseInt(qtyInput?.value) || 0;
                const promotionalPrice = parseFloat(priceEl?.dataset.productPrice || 0);
                const originalPrice = parseFloat(originalPriceEl?.dataset.productOriginalPrice || promotionalPrice);

                if (productId && quantity > 0) {
                    products.push({
                        product_id: productId,
                        quantity: quantity,
                        promotional_price: promotionalPrice,
                        original_price: originalPrice,
                        promotion_enabled: this.promotion.enabled && this.promotion.percentage > 0 && originalPrice > promotionalPrice
                    });
                }
            });
        }

        // Get drainage products
        if (drainageChecked) {
            const drainageContainers = container.querySelectorAll('[data-product-type="drainage"]');
            drainageContainers.forEach(prodContainer => {
                const qtyInput = prodContainer.querySelector('input[type="number"]');
                const priceEl = prodContainer.querySelector('[data-product-price]');
                const originalPriceEl = prodContainer.querySelector('[data-product-original-price]');
                const productId = qtyInput?.dataset.productId || '';
                const quantity = parseInt(qtyInput?.value) || 0;
                const promotionalPrice = parseFloat(priceEl?.dataset.productPrice || 0);
                const originalPrice = parseFloat(originalPriceEl?.dataset.productOriginalPrice || promotionalPrice);

                if (productId && quantity > 0) {
                    products.push({
                        product_id: productId,
                        quantity: quantity,
                        promotional_price: promotionalPrice,
                        original_price: originalPrice,
                        promotion_enabled: this.promotion.enabled && this.promotion.percentage > 0 && originalPrice > promotionalPrice
                    });
                }
            });
        }

        return products;
    }

    /**
     * Add selected products to cart
     *
     * @param {DocumentFragment} fragment - Results fragment
     */
    async addToCart(fragment) {
        const selectedProducts = this.getSelectedProducts(fragment);

        if (selectedProducts.length === 0) {
            this.#notifyUser({
                text: 'Wybierz przynajmniej jeden produkt',
                duration: 3000,
                gravity: 'top',
                position: 'right',
                style: {
                    background: 'linear-gradient(to right, #ff5f6d, #ffc371)',
                }
            });
            return;
        }

        // Add each product to cart
        for (const product of selectedProducts) {
            try {
                const formData = new FormData();
                formData.append('product_id', product.product_id);
                formData.append('quantity', product.quantity);
                formData.append('action', 'sage_add_to_cart');
                formData.append('nonce', window?.app?.nonce);

                // Add promotional price information as cart item data
                if (product.promotion_enabled && product.promotional_price < product.original_price) {
                    formData.append('substrate_calculator_promotion_enabled', '1');
                    formData.append('substrate_calculator_promotional_price', product.promotional_price.toString());
                    formData.append('substrate_calculator_original_price', product.original_price.toString());
                    formData.append('substrate_calculator_discount_percentage', this.promotion.percentage.toString());
                }

                const response = await fetch(window?.app?.ajaxUrl, {
                    method: 'POST',
                    body: formData,
                    credentials: 'same-origin',
                });

                const result = await response.json();

                if (result?.success) {
                    Mediator.publish('ajax_sage_change_cart', result);
                } else {
                    Mediator.publish('ajax_sage_change_cart_error', result);
                }
            } catch (error) {
                console.error('Error adding to cart:', error);
            }
        }

        // Show success message on the right with "Zobacz koszyk" button (matching related products style)
        this.#notifyUserWithCartButton({
            text: 'Produkty zostały dodane do koszyka',
            duration: 5000,
            gravity: 'top',
            position: 'right',
            style: {
                background: 'linear-gradient(to right, rgb(0, 176, 155), rgb(150, 201, 61))',
            }
        });
    }

    /**
     * Show toast notification
     *
     * @param {Object} config - Toast configuration
     */
    #notifyUser(config) {
        if (!config || !config.text || config.text === '') {
            return;
        }
        const toast = Toastify(config);
        toast.showToast();

        // Handle sticky cart toggle button click
        setTimeout(() => {
            const toggleBtn = document.querySelector('[data-sticky-cart-toggle]');
            if (toggleBtn) {
                toggleBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    Mediator.publish("update_sticky_cart_view", "toggle");
                });
            }
        }, 100);
    }

    /**
     * Show toast notification with cart button
     *
     * @param {Object} config - Toast configuration
     */
    #notifyUserWithCartButton(config) {
        if (!config || !config.text || config.text === '') {
            return;
        }

        // Create toast with cart notification structure matching product lists
        const toast = Toastify({
            text: `
                <div class="cart-notification-content">
                    <div class="cart-notification-text">
                        <div class="cart-notification-title">Pomyślnie dodano</div>
                        <div class="cart-notification-subtitle">${config.text}</div>
                    </div>
                    <button type="button" class="cart-notification-button" data-sticky-cart-toggle="">Zobacz koszyk</button>
                </div>
            `,
            duration: config.duration || 5000,
            gravity: 'top',
            position: 'right',
            style: config.style || {
                background: 'linear-gradient(to right, rgb(0, 176, 155), rgb(150, 201, 61))',
            },
            escapeMarkup: false,
            close: false
        });

        toast.showToast();

        // Handle sticky cart toggle button click
        setTimeout(() => {
            const toggleBtn = toast.toastElement.querySelector('[data-sticky-cart-toggle]');
            if (toggleBtn) {
                toggleBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    Mediator.publish("update_sticky_cart_view", "toggle");
                    toast.hideToast();
                });
            }
        }, 100);
    }

    /**
     * Render related products (cross-sells)
     *
     * @param {DocumentFragment} fragment - Results fragment
     */
    renderRelatedProducts(fragment) {
        const crossSellProducts = this.calculatorProducts?.cross_sell || [];

        if (!crossSellProducts || crossSellProducts.length === 0) {
            return;
        }

        const relatedContainer = fragment.querySelector('[data-substrate-calculator="related-products"]');
        const relatedList = fragment.querySelector('[data-substrate-calculator="related-products-list"]');

        if (!relatedContainer || !relatedList) {
            return;
        }

        // Show the related products section
        relatedContainer.classList.remove('hidden');

        // Clear existing content
        relatedList.innerHTML = '';

        // Render each related product
        crossSellProducts.forEach(product => {
            const productElement = this.createRelatedProductElement(product);
            if (productElement) {
                relatedList.appendChild(productElement);

                // Add click handler for AJAX add-to-cart
                const button = productElement.querySelector('[data-related-product-button]');
                if (button) {
                    button.addEventListener('click', async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation(); // Prevent CartEventManager from also handling this click
                        const productId = button.getAttribute('data-ajax-add-to-cart');
                        if (productId) {
                            await this.addRelatedProductToCart(productId, product);
                        }
                    });
                }
            }
        });
    }

    /**
     * Add related product to cart via AJAX
     *
     * @param {string} productId - Product ID
     * @param {Object} product - Product data for notifications
     */
    async addRelatedProductToCart(productId, product) {
        try {
            const formData = new FormData();
            formData.append('product_id', productId);
            formData.append('quantity', 1);
            formData.append('action', 'sage_add_to_cart');
            formData.append('nonce', window?.app?.nonce);

            const response = await fetch(window?.app?.ajaxUrl, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
            });

            const result = await response.json();

            if (result?.success) {
                // Display notification from PHP response and update cart
                this.#notifyUser(result?.data?.notice);
                Mediator.publish('ajax_sage_change_cart', result);
            } else {
                Mediator.publish('ajax_sage_change_cart_error', result);
            }
        } catch (error) {
            console.error('Error adding related product to cart:', error);
        }
    }

    /**
     * Create a related product element using template
     *
     * @param {Object} product - Product data
     * @returns {HTMLElement} Product element
     */
    createRelatedProductElement(product) {
        const fragment = this.templateManager.clone('relatedProductItem');
        if (!fragment) return null;

        const container = fragment.firstElementChild;

        // Product image
        const img = container.querySelector('[data-related-product-image]');
        if (img) {
            img.src = product.image || '';
            img.alt = product.name;
        }

        // Product name
        const name = container.querySelector('[data-related-product-name]');
        if (name) {
            if (product.url) {
                // Create a link element
                const link = document.createElement('a');
                link.href = product.url;
                link.textContent = product.name;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.className = 'font-semibold text-brand-secondary-950 hover:text-brand-secondary-700 transition-colors';
                name.innerHTML = '';
                name.appendChild(link);
            } else {
                name.textContent = product.name;
            }
        }

        // Product description
        const description = container.querySelector('[data-related-product-description]');
        if (description) {
            description.textContent = product.short_description || product.description || '';
        }

        // Button with price
        const button = container.querySelector('[data-related-product-button]');
        if (button) {
            const productId = product.id;
            button.setAttribute('data-ajax-add-to-cart', productId.toString());
            const formattedPrice = product.price ? `${parseFloat(product.price).toFixed(2).replace('.', ',')} ${t('currency')}` : '';
            button.textContent = formattedPrice ? t('add_for_price').replace('%s', formattedPrice) : t('add_to_cart');
        }

        return container;
    }

    /**
     * Reset lock state
     */
    resetLockState() {
        this.quantityLockState = {
            substrate: {locked: true, defaults: {}},
            drainage: {locked: true, defaults: {}}
        };
    }
}
